/**
 * Area unit conversion utilities for Collateral Map feature.
 * Lao PDR standard units: m², ໄຮ່, ງານ, ຕາຣາງວາ
 * 
 * [COLLATERAL MAP] Added 2026-07-14
 */

import { COLLATERAL_MAP } from "../../../shared/constants/collateralMap";

const { RAI, NGAN, WA } = COLLATERAL_MAP.AREA_UNITS;

/** Convert m² to ໄຮ່ */
export function sqmToRai(sqm: number): number {
  return sqm / RAI;
}

/** Convert m² to ງານ */
export function sqmToNgan(sqm: number): number {
  return sqm / NGAN;
}

/** Convert m² to ຕາຣາງວາ */
export function sqmToWa(sqm: number): number {
  return sqm / WA;
}

/**
 * Format area in Lao units.
 * e.g. 3200 m² → "3,200 m² (2 ໄຮ່ 0 ງານ 0 ຕ.ວ.)"
 */
export function formatAreaLao(sqm: number): string {
  if (!isFinite(sqm) || sqm < 0) return "0 m²";

  const totalRai = Math.floor(sqm / RAI);
  const remainAfterRai = sqm % RAI;
  const totalNgan = Math.floor(remainAfterRai / NGAN);
  const remainAfterNgan = remainAfterRai % NGAN;
  const totalWa = Math.floor(remainAfterNgan / WA);

  // Use en-US for comma thousands separator (lo-LA uses period which is confusing)
  const sqmFormatted = Math.round(sqm).toLocaleString("en-US");

  // Only show unit breakdown if >= 1 rai or >= 1 ngan
  if (totalRai === 0 && totalNgan === 0) {
    // Show in m² only (or m² + ຕ.ວ. if >= 1 wa)
    if (totalWa === 0) return `${sqmFormatted} m²`;
    return `${sqmFormatted} m² (${totalWa} ຕ.ວ.)`;
  }

  const parts: string[] = [];
  if (totalRai > 0) parts.push(`${totalRai} ໄຮ່`);
  if (totalNgan > 0) parts.push(`${totalNgan} ງານ`);
  if (totalWa > 0) parts.push(`${totalWa} ຕ.ວ.`);

  return `${sqmFormatted} m² (${parts.join(" ")})`;
}

/**
 * Format area as a compact multi-line display for the map info bar.
 * Returns an array of { label, value } pairs.
 */
export function formatAreaBreakdown(sqm: number): Array<{ label: string; value: string }> {
  if (!isFinite(sqm) || sqm < 0) {
    return [{ label: "ເນື້ອທີ່", value: "0 m²" }];
  }

  const totalRai = Math.floor(sqm / RAI);
  const remainAfterRai = sqm % RAI;
  const totalNgan = Math.floor(remainAfterRai / NGAN);
  const remainAfterNgan = remainAfterRai % NGAN;
  const totalWa = Math.floor(remainAfterNgan / WA);

  return [
    { label: "m²", value: Math.round(sqm).toLocaleString("lo-LA") },
    { label: "ໄຮ່", value: totalRai.toLocaleString("lo-LA") },
    { label: "ງານ", value: totalNgan.toLocaleString("lo-LA") },
    { label: "ຕ.ວ.", value: totalWa.toLocaleString("lo-LA") },
  ];
}

/**
 * Calculate perimeter of a polygon from Leaflet LatLng array.
 * Uses Leaflet's built-in distanceTo (Haversine) for accuracy.
 * @param latLngs - Array of Leaflet LatLng objects (must have .distanceTo method)
 */
export function calcPerimeterFromLatLngs(latLngs: Array<{ distanceTo: (other: any) => number }>): number {
  if (!latLngs || latLngs.length < 2) return 0;
  let perimeter = 0;
  for (let i = 0; i < latLngs.length; i++) {
    const next = (i + 1) % latLngs.length;
    perimeter += latLngs[i].distanceTo(latLngs[next]);
  }
  return perimeter;
}

/**
 * Format perimeter in meters or km.
 */
export function formatPerimeter(meters: number): string {
  if (!isFinite(meters) || meters < 0) return "0 m";
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}
